import React, { useEffect, useState, useMemo } from "react";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Box, Button, Grid, makeStyles } from "@material-ui/core/";
import TableFooter from "@material-ui/core/TableFooter";
import TablePagination from "@material-ui/core/TablePagination";
import Tooltip from "@material-ui/core/Tooltip";
import EditIcon from "@material-ui/icons/Edit";
import VisibilityIcon from '@material-ui/icons/Visibility';
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import Fab from "@material-ui/core/Fab";
import AddIcon from "@material-ui/icons/Add";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import DnerhsApi, { CancelToken } from "../../../api/DnerhsApi";
import { Link, useParams } from "react-router-dom";
import DeleteDialog from "../../../dialogs/DeleteDialog";
import history from "../../../utils/History";
import Alert from "../../../components/Alert";
import ResponseUtils from "../../../utils/ResponseUtils";
import useAuth from "../../../hooks/Auth";
import Roles from "../../../constants/Roles";

const useStyles = makeStyles((theme) => ({
  title: {
    margin: theme.spacing(3),
  },
  button: {
    margin: theme.spacing(),
  },
  searchBox: {
    marginBottom: theme.spacing(2),
  },
}));

const List = () => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allRows, setAllRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [toDelete, setToDelete] = useState();
  const { hasRole, hasAnyRole } = useAuth();
  const params = useParams();
  const [isPostgrado, setIsPostgrado] = useState(false);

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return allRows;
    const term = searchTerm.toLowerCase();
    return allRows.filter((row) => {
      const ci = (row.estudiante?.cedulaIdentidad ?? "").toLowerCase();
      const nombres = (row.estudiante?.nombres ?? "").toLowerCase();
      const apellidos = (row.estudiante?.apellidos ?? "").toLowerCase();
      const carrera = (isPostgrado
        ? "postgrado " + (row.nombrePostgrado?.descripcion ?? "")
        : (row.carrera?.descripcion ?? "")
      ).toLowerCase();
      const materia = (row.materia ?? "").toLowerCase();
      return (
        ci.includes(term) ||
        nombres.includes(term) ||
        apellidos.includes(term) ||
        carrera.includes(term) ||
        materia.includes(term)
      );
    });
  }, [allRows, searchTerm, isPostgrado]);

  const displayedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  useEffect(() => {
    const load = () => {
      try {
        loadData();
      } catch (error) {
        console.log(error);
      }
    };

    load();
  }, []);

  const loadData = async () => {
    let source = CancelToken.source();
    try {
      const { data: convenioResponseData } = await DnerhsApi.get(`/convenios/${params.convenioId}`);
      if(convenioResponseData.categoria==="Postgrado"){
        setIsPostgrado(true);
      }
      let url = `/convenios/${params.convenioId}/estudiantes/page/?page=0&pageSize=10000`;
      const response = await DnerhsApi(url, {
        cancelToken: source.token,
      });
      const { content } = response.data;
      setAllRows(content);
    } catch (error) {
      console.log(error);
      if (error.response) {
        showServerError(error);
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const deleteOne = async () => {
    let source = CancelToken.source();
    try {
      let url = `/convenios/estudiantes/${toDelete}`;
      await DnerhsApi.delete(url, {
        cancelToken: source.token,
      });

      Alert.show({ message: "Eliminado exitosamente.", type: "success" })

      await loadData();

    } catch (error) {
      console.log(error);
      if (error.response) {
        showServerError(error);
      }
    }
    setOpenDialog(false);
    setToDelete();
  };

  const showServerError = (error) => {
    let message = "Ocurrió un error inesperado, por favor vuelva a reintentar";
    if (error.response) {
      message = ResponseUtils.getMessageError(error.response);
    }

    Alert.show({
      message,
      type: "error"
    })
  }

  const handleOpenDialog = (id) => {
    setOpenDialog(true);
    setToDelete(id);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12}>
        <Paper>
          <Box p={1}>
            <Typography
              variant="h5"
              color="primary"
              align="center"
              className={classes.title}
            >
              Estudiantes
            </Typography>
            <Box className={classes.searchBox}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Buscar por CI, nombres, apellidos, carrera o materia..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="left">CI</TableCell>
                  <TableCell align="left">Nombres</TableCell>
                  <TableCell align="left">Apellidos</TableCell>
                  <TableCell align="left">Carrera</TableCell>
                  <TableCell align="left">Materia</TableCell>
                  <TableCell align="center">Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {searchTerm.trim()
                        ? "No se encontraron coincidencias para la búsqueda."
                        : "No hay registros."}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedRows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell align="left">
                      {row.estudiante.cedulaIdentidad}
                    </TableCell>
                    <TableCell align="left">
                      {row.estudiante.nombres}
                    </TableCell>
                    <TableCell align="left">
                      {row.estudiante.apellidos}
                    </TableCell>
                    <TableCell align="left">
                      {(isPostgrado? "Postgrado - " + row.nombrePostgrado.descripcion:  row.carrera.descripcion)}

                    </TableCell>
                    <TableCell align="left">
                      {row.materia}
                    </TableCell>
                    <TableCell align="center">

                      {hasRole(Roles.ROLE_USER) &&
                        <>
                          <Tooltip title="Editar" aria-label="editar">
                            <IconButton
                              size="small"
                              aria-label="editar"
                              color="primary"
                              onClick={() => history.push({
                                pathname: `/convenios/estudiantes/editar/${row.id}`,
                                state: {
                                  edit: true
                                }
                              })}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Eliminar" aria-label="eliminar">
                            <IconButton
                              size="small"
                              aria-label="eliminar"
                              color="secondary"
                              onClick={() => handleOpenDialog(row.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      }

                      {hasAnyRole([Roles.ROLE_ADMIN, Roles.ROLE_DNERHS]) &&
                        <>
                          <Tooltip title="Ver" aria-label="Ver">
                            <IconButton
                              size="small"
                              aria-label="ver"
                              color="primary"
                              onClick={() => history.push({
                                pathname: `/convenios/estudiantes/editar/${row.id}`,
                                state: {
                                  readOnly : true 
                                }
                              })}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      }

                    </TableCell>
                  </TableRow>
                ))
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 15, 20]}
                    colSpan={6}
                    count={filteredRows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: { "aria-label": "Registros por página" },
                      native: false,
                    }}
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} de ${count}`
                    }
                    labelRowsPerPage="Registros por página"
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableRow>
              </TableFooter>
            </Table>
            <Grid
              container
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Grid item className={classes.button}>
                <Button
                  variant="contained"
                  color="default"
                  onClick={() => history.goBack()}
                >
                  Atrás
                </Button>
              </Grid>
              {hasRole(Roles.ROLE_USER) && (
                <Grid item className={classes.button}>
                  <Link to={`/convenios/estudiantes/crear/${params.convenioId}`}>
                    <Fab color="primary" aria-label="nuevo">
                      <Tooltip title="Nuevo estudiante" aria-label="nuevo">
                        <AddIcon />
                      </Tooltip>
                    </Fab>
                  </Link>
                </Grid>
              )}
            </Grid>
          </Box>
          <DeleteDialog
            open={openDialog}
            onClose={handleCloseDialog}
            onAccept={deleteOne}
            aria-labelledby="form-dialog-title"
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default List;
